// ============================================================
// THE KILL-TEAMS (plan modes-rework, 2026-10-27; Squad Mode stage 6 before
// it): a kill-team is read off the roster's SHEETS - the team record every
// member carries holds its block (`killTeam/block`: the Oath copied from the
// library's Tactics, the support range, the GM's adjustment) and one member
// holds `leads`. Nothing is sworn: the starting Cohesion is worked out LIVE
// from the leader's sheet, the Oath's bonus and the adjustment.
//
// The session keeps one document of its own, `direction/teams` (gm-writes:
// players read it, the GM alone writes it): the GM's answer, per stage and
// set of brothers, to "who leads when the leader is elsewhere" (ruled
// 2026-10-23) - session state no sheet can hold.
//
//   { v: 2, actingLeaders: { [tag]: { [stageKey]: combatantId } } }
//
// An older session's document (v1, the sworn setup) reads as empty: old
// sessions are not carried over (the user's ruling, 2026-10-27).
//
// Who is a BROTHER (D8): a member of the team with a person's body who is
// not somebody's control-network subordinate - servitors on the side are
// allies, never kill-team members - and still in the fight.
//
// Pure. Zero React dependency.
// ============================================================
import type { Combatant } from '../roster/types';
import { controlOf } from '../roster/control';
import { isInTheFight } from '../roster/inTheFight';
import { projectDefender } from '../roster/projection';
import { resolveBodyShape, isCrowd } from '../bodyShape';
import { playedBlock, tagConflicts, teamOf, teamsIn, type Team } from '../roster/team';
import { startingCohesion, type CohesionLine } from './cohesion';
import type { KillTeamBlock, OathCopy } from './block';

/** A kill-team as play reads it: derived from the sheets (and the session's answers), never stored. */
export interface KillTeam {
  tag: string;
  /** The Oath it plays (its abilities copied whole); null while none is chosen - nobody can enter Squad Mode. */
  oath: OathCopy | null;
  /** The library version of the Tactics the Oath was copied from (0 for none): what a running ability snapshots. */
  rev: number;
  /** The brother whose sheet says he leads; null for nobody. */
  leaderId: string | null;
  /** Support range, metres: a brother needs another this close to enter Squad Mode. */
  rangeM: number;
  adjustment: number;
  /** The starting Cohesion, worked out now, and where every point came from. */
  start: { start: number; breakdown: CohesionLine[] };
  /** `stageKey` -> the combatant the GM named to lead that set of brothers on that stage. */
  actingLeaders: Record<string, string>;
}

/** Every kill-team on the roster, by tag. */
export interface KillTeams {
  killTeams: Record<string, KillTeam>;
}

/** The session's own document: the GM's acting-leader answers, by tag. */
export interface TeamsDoc {
  v: 2;
  actingLeaders: Record<string, Record<string, string>>;
}

export const emptyTeamsDoc = (): TeamsDoc => ({ v: 2, actingLeaders: {} });

type Raw = Record<string, unknown>;

/** Answers that are answers: string ids under string keys. */
const answers = (raw: unknown): Record<string, string> =>
  Object.fromEntries(Object.entries((raw ?? {}) as Raw).filter((e): e is [string, string] => typeof e[1] === 'string'));

/** The stored document, validated; anything unreadable, or an older session's (v1), is an empty one. */
export function readTeamsDoc(raw: unknown): TeamsDoc {
  const r = (raw ?? null) as Raw | null;
  if (r?.v !== 2) return emptyTeamsDoc();
  const byTag = Object.entries((r.actingLeaders ?? {}) as Raw).map(([tag, entry]) => [tag, answers(entry)] as const);
  return { v: 2, actingLeaders: Object.fromEntries(byTag.filter(([, a]) => Object.keys(a).length)) };
}

/** The document with the GM's answer for one stage and set of brothers kept. */
export const withActingLeader = (doc: TeamsDoc, tag: string, key: string, leaderId: string): TeamsDoc => ({
  ...doc,
  actingLeaders: { ...doc.actingLeaders, [tag]: { ...doc.actingLeaders[tag], [key]: leaderId } },
});

/**
 * Every kill-team on the roster: each team whose sheets carry a block. A tag two teams wear is none until it is Split
 * (it would pour two squads into one pool). Nothing here depends on the roster's ORDER - a reorder (a rejoin, a
 * reseed) must never change the Oath, which would reset the pool (the stage review).
 */
export function killTeamsOf(roster: readonly Combatant[], doc: TeamsDoc): KillTeams {
  const conflicted = new Set(tagConflicts(roster).map((c) => c.tag));
  const killTeams: Record<string, KillTeam> = {};
  for (const team of teamsIn(roster)) {
    const leader = leaderIn(team);
    const block = playedBlock(team, leader);
    if (block && !conflicted.has(team.tag)) killTeams[team.tag] = killTeamFrom(team.tag, block, leader, doc);
  }
  return { killTeams };
}

function killTeamFrom(tag: string, block: KillTeamBlock, leader: Combatant | null, doc: TeamsDoc): KillTeam {
  const oath = block.oath ?? null;
  const adjustment = block.adjustment ?? 0;
  return {
    tag,
    oath,
    rev: block.tactics?.origin?.version ?? 0,
    leaderId: leader?.id ?? null,
    rangeM: block.rangeM,
    adjustment,
    start: startingCohesion(leader?.sheet ?? null, oath, adjustment),
    actingLeaders: doc.actingLeaders[tag] ?? {},
  };
}

/** The brother whose sheet says he leads; of two such sheets, the lower id (never the roster's order). */
function leaderIn(team: Team<Combatant>): Combatant | null {
  const leaders = team.members.filter((c) => teamOf(c)?.leads === true && isBrother(c, team.tag));
  return leaders.sort((a, b) => a.id.localeCompare(b.id))[0] ?? null;
}

/** Is this combatant a brother of the kill-team `tag` (D8)? Not whether they are still in the fight. */
export function isBrother(c: Combatant, tag: string): boolean {
  if (teamOf(c)?.tag !== tag) return false;
  const control = controlOf(c);
  if (control?.tag && !control.isController) return false;
  const body = projectDefender(c);
  return resolveBodyShape(body) === 'humanoid' && !isCrowd(body);
}

/** The brothers of `tag` on this roster who are still in the fight. */
export const brothersIn = (roster: readonly Combatant[], tag: string): Combatant[] =>
  roster.filter((c) => isBrother(c, tag) && isInTheFight(c));

/** The key the GM's acting-leader answer is kept under: the stage and the set of brothers on it. */
export function stageKey(sceneId: string | null | undefined, brotherIds: readonly string[]): string {
  return `${sceneId || '-'}|${[...brotherIds].sort().join(',')}`;
}

/**
 * Who leads these brothers here: the leader when he is among them, else the GM's answer for this exact set on this
 * stage; null when the GM has not answered (the question is asked once, then kept until the set changes).
 */
export function leaderHere(
  kt: KillTeam,
  sceneId: string | null | undefined,
  brotherIds: readonly string[],
): string | null {
  if (kt.leaderId && brotherIds.includes(kt.leaderId)) return kt.leaderId;
  const answer = kt.actingLeaders[stageKey(sceneId, brotherIds)];
  return answer && brotherIds.includes(answer) ? answer : null;
}

/** Does the GM owe an answer: a squad of two or more here, without its leader and without one named for it? */
export const needsActingLeader = (kt: KillTeam, sceneId: string | null | undefined, brotherIds: readonly string[]) =>
  brotherIds.length >= 2 && leaderHere(kt, sceneId, brotherIds) === null;
